import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  EmbeddedViewRef,
  inject,
  signal,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { MapOverviewPopupPanelComponent } from '@pages/map-overview-popup-panel/map-overview-popup-panel.component';
import * as L from 'leaflet';
import { IconOptions, Marker } from 'leaflet';
import {
  BuildingMapDto,
  BuildingService,
  ConditionDto,
  UserDetailDto,
} from '@artemis-software/wr-api';
import { firstValueFrom } from 'rxjs';
import { tryWithLoadingSignal } from '@/utils/async-utils';
import { loader } from '@/utils/signals/loader';
import { currentTenantId, currentUser, isExternalTenant } from '@/utils/admin-utils';
import TenantTypeEnum = UserDetailDto.TenantTypeEnum;

export const mapUrls = [
  'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
];

const iconOptions: Partial<IconOptions> = {
  iconSize: [50, 50],
  iconAnchor: [25, 45],
  popupAnchor: [0, -30],
};

export const primaryIcon = L.icon({
  ...iconOptions,
  iconUrl: 'assets/icons/map-pin-primary.png',
});

export const successIcon = L.icon({
  ...iconOptions,
  iconUrl: 'assets/icons/map-pin-success.png',
});

export const warningIcon = L.icon({
  ...iconOptions,
  iconUrl: 'assets/icons/map-pin-warning.png',
});

export const dangerIcon = L.icon({
  ...iconOptions,
  iconUrl: 'assets/icons/map-pin-danger.png',
});

export const unknownIcon = L.icon({
  ...iconOptions,
  iconUrl: 'assets/icons/map-pin-gray.png',
});

const getIconForCondition = (condition: ConditionDto.ConditionEnum) => {
  switch (condition) {
    case ConditionDto.ConditionEnum.MinorDefects:
      return warningIcon;
    case ConditionDto.ConditionEnum.MajorDefects:
      return dangerIcon;
    case ConditionDto.ConditionEnum.NoDefects:
      return successIcon;
    default:
      return unknownIcon;
  }
};

@Component({
  standalone: false,
  selector: 'wr-map-overview',
  templateUrl: './map-overview.component.html',
  styleUrls: ['./map-overview.component.scss'],
})
export class MapOverviewComponent implements AfterViewInit {

  private readonly buildingService = inject(BuildingService);
  private readonly viewContainerRef = inject(ViewContainerRef);

  readonly currentUser = currentUser();
  readonly isExternalTenant = isExternalTenant();
  readonly tenantId = currentTenantId();

  readonly showAll = computed(() => {
    const currentUser = this.currentUser();
    if (!currentUser) {
      return false;
    }
    return currentUser.tenantType === TenantTypeEnum.Internal;
  });

  readonly hiddenContainer = viewChild<ElementRef>('hidden');

  private map!: L.Map;
  private markers: L.Marker[] = [];
  private layerGroup = L.layerGroup();

  readonly loading = loader();
  readonly buildingCount = signal(0);
  readonly selectedTenantId = signal<string | undefined>(undefined);

  constructor() {
    effect(() => {
      const tenantId = this.isExternalTenant() ? this.tenantId() : this.selectedTenantId();
      this.fetchBuildings(tenantId);
    });
  }

  ngAfterViewInit() {
    this.initializeMap();
  }

  onTenantChange(tenantId: string | undefined): void {
    this.selectedTenantId.set(tenantId);
  }

  private async fetchBuildings(ownerTenantId: string | undefined) {
    this.markers.length = 0;
    this.layerGroup.clearLayers();
    await tryWithLoadingSignal(this.loading, async () => {
      const buildings = await firstValueFrom(this.buildingService.findAllForMap({ ownerTenantId }));
      buildings.forEach(building => {
        const marker = this.createMarkerForBuilding(building);
        if (marker) {
          this.markers.push(marker);
        }
      });
      this.buildingCount.set(this.markers.length);
      this.addMarkersToMap();
      this.centerMapOnMarkers();
    });
  }

  private createMarkerForBuilding(building: BuildingMapDto): L.Marker | null {
    const lat = building.coordinates?.latitude;
    const long = building.coordinates?.longitude;

    if (lat && long) {
      const marker = L.marker([lat, long], {
        icon: getIconForCondition(building.buildingCondition.condition),
      });
      marker.options.title = building.name;
      this.bindMarkerPopupContent(marker, building);
      return marker;
    }
    return null;
  }

  private initializeMap() {
    this.map = L.map('map');
    L.tileLayer(mapUrls[0]).addTo(this.map);
  }

  private addMarkersToMap() {
    this.markers.forEach(marker => this.layerGroup.addLayer(marker));
    this.layerGroup.addTo(this.map);
  }

  private centerMapOnMarkers() {
    if (this.markers.length === 0) {
      return;
    }
    const bounds = L.latLngBounds(this.markers.map(marker => marker.getLatLng()));
    this.map.fitBounds(bounds);
  }

  private bindMarkerPopupContent(marker: Marker, building: BuildingMapDto) {
    marker.on('click', () => {
      if (!marker.getPopup()) {
        const popupPanelComponent = this.viewContainerRef.createComponent(MapOverviewPopupPanelComponent);
        popupPanelComponent.instance.building.set(building);
        const domElem: HTMLElement = (popupPanelComponent.hostView as EmbeddedViewRef<unknown>).rootNodes[0] as HTMLElement;
        this.hiddenContainer()?.nativeElement.append(domElem);
        const popupContent = L.popup().setContent(domElem);
        marker.bindPopup(popupContent).openPopup();
      }
    });
  }

  protected readonly TenantTypeEnum = TenantTypeEnum;
}
